/**
 * Every route in docs/SPEC.md §5.1. Screens load on demand (one chunk each), so a student on
 * a Chromebook never downloads teacher code. Guards are layout routes: RequireTeacher /
 * RequireStudent render the screen or redirect to the right sign-in page with a notice.
 *
 * Each route also names the string bundles its screens read (i18n/index.tsx): they load in
 * the viewer's language, in parallel with the screen's code. A screen that reads an area it
 * did not ask for still works (it suspends until the strings arrive) but pays a round trip.
 */
import { Suspense, useEffect, useRef, type ComponentType } from 'react'
import { Navigate, Outlet, ScrollRestoration, useLocation, type RouteObject } from 'react-router'
import { LoadingBlock } from '../design/components'
import { loadMessages, type Bundle } from '../i18n'
import { RequireCurator, RequireHousehold } from '../museum/guards'
import type { FocusSwitchState } from '../museum/components/ViewSwitch'
import { RequireStudent, RequireTeacher } from '../session/guards'
import { AppErrorPage, RouteErrorPage } from './AppErrorPage'
import { RecoveringErrorBoundary } from './ErrorBoundary'
import { isHubSite } from './site'

type ScreenModule = { default: ComponentType }

/** Route-level code splitting: the module's default export becomes the route component. */
function screen(
  load: () => Promise<ScreenModule>,
  bundles: readonly Bundle[],
): RouteObject['lazy'] {
  return async () => {
    const [module] = await Promise.all([load(), loadMessages(bundles)])
    return { Component: module.default }
  }
}

/**
 * Strings per audience. Teacher chrome reads `public` (Help) and screens preview the world.
 * The building reads only the world's labels; the wall and the list localize medium lines
 * with the Reader's words, so they load `learn` too.
 */
const PUBLIC: Bundle[] = ['site']
const TEACHER: Bundle[] = ['teach', 'world', 'learn', 'site']
const BUILDING: Bundle[] = ['world']
const WORLD: Bundle[] = ['world', 'learn']
const STUDENT: Bundle[] = ['world', 'learn']
const FAMILY: Bundle[] = ['world', 'learn', 'site']
/** Family setup (docs/museum-spec.md §5): the museum's screens show the room wall and reader. */
const MUSEUM: Bundle[] = ['museum', 'world', 'learn']
const OFFICE: Bundle[] = ['office', 'museum', 'world', 'learn']
/** The Curator's Hub (family setup, level 2): its own strings plus the public shell's. */
const HUB: Bundle[] = ['hub', 'site']

/**
 * After an in-app navigation, move focus to the new page's <main> so screen readers start
 * at the top of the new content instead of on the link that was pressed. A navigation that hands
 * focus to the museum's 2D/3D switch (`focusViewSwitch`: choosing 3D on the map walks into a room)
 * keeps it there: the switch takes focus itself, and this effect runs after the page's own.
 */
function useFocusMainOnNavigate(): void {
  const { pathname, state } = useLocation()
  // Compare with the last path rather than a "first run" flag: StrictMode runs effects twice
  // on mount, and a flag would then steal focus on the very first page load.
  const shownPath = useRef(pathname)
  const toSwitch = (state as FocusSwitchState | null)?.focusViewSwitch === true
  useEffect(() => {
    if (shownPath.current === pathname) return
    shownPath.current = pathname
    if (toSwitch) return
    document.getElementById('main')?.focus({ preventScroll: true })
  }, [pathname, toSwitch])
}

function RootLayout() {
  const { pathname } = useLocation()
  useFocusMainOnNavigate()
  return (
    <>
      <RecoveringErrorBoundary resetKey={pathname} fallback={() => <AppErrorPage />}>
        <Suspense fallback={<LoadingBlock />}>
          <Outlet />
        </Suspense>
      </RecoveringErrorBoundary>
      <ScrollRestoration />
    </>
  )
}

export const routes: RouteObject[] = [
  {
    path: '/',
    Component: RootLayout,
    errorElement: <RouteErrorPage />,
    HydrateFallback: LoadingBlock,
    children: [
      // Public (the family setup's front door, or the school's landing page)
      isHubSite()
        ? // The Curator's Hub's site (VITE_GALLEY_SITE=hub): its front page is the hub's.
          { index: true, element: <Navigate to="/hub" replace /> }
        : { index: true, lazy: screen(() => import('./HomeGate'), []) },
      { path: 'privacy', lazy: screen(() => import('../public/Privacy'), PUBLIC) },
      { path: 'accessibility', lazy: screen(() => import('../public/Accessibility'), PUBLIC) },
      { path: 'tech-kit', lazy: screen(() => import('../teacher/screens/TechKit'), TEACHER) },

      // Teacher
      {
        path: 't',
        element: <RequireTeacher />,
        children: [
          { index: true, lazy: screen(() => import('../teacher/screens/Home'), TEACHER) },
          { path: 'new', lazy: screen(() => import('../teacher/screens/NewGalley'), TEACHER) },
          {
            path: 'g/:gid',
            lazy: screen(() => import('../teacher/screens/GalleyDashboard'), TEACHER),
          },
          { path: 'g/:gid/add', lazy: screen(() => import('../teacher/screens/AddRoom'), TEACHER) },
          {
            path: 'g/:gid/rooms/:rid',
            lazy: screen(() => import('../teacher/screens/RoomManage'), TEACHER),
          },
          {
            path: 'g/:gid/walk/new',
            lazy: screen(() => import('../teacher/screens/StartWalk'), TEACHER),
          },
          {
            path: 'g/:gid/walk/:wid',
            lazy: screen(() => import('../teacher/screens/WalkConsole'), TEACHER),
          },
          {
            path: 'g/:gid/walk/:wid/review',
            lazy: screen(() => import('../teacher/screens/ReviewNotes'), TEACHER),
          },
          {
            path: 'g/:gid/settings',
            lazy: screen(() => import('../teacher/screens/GalleySettings'), TEACHER),
          },
          {
            path: 'g/:gid/cards',
            lazy: screen(() => import('../teacher/screens/SignInCards'), TEACHER),
          },
        ],
      },
      {
        path: 'present/:gid',
        element: <RequireTeacher />,
        children: [
          { index: true, lazy: screen(() => import('../projector/screens/Present'), TEACHER) },
        ],
      },

      // World (teacher, student member, or family token ?f=) — the server decides access.
      { path: 'g/:gid', lazy: screen(() => import('../world/screens/BuildingScreen'), BUILDING) },
      { path: 'g/:gid/r/:rid', lazy: screen(() => import('../world/screens/RoomScreen'), WORLD) },
      { path: 'g/:gid/p/:pid', lazy: screen(() => import('../world/screens/ReaderScreen'), WORLD) },
      { path: 'g/:gid/list', lazy: screen(() => import('../world/screens/ListScreen'), WORLD) },

      // Students
      { path: 'join', lazy: screen(() => import('../student/screens/Join'), STUDENT) },
      // Google sign-in for a student in several classes lands here to pick one.
      { path: 'join/choose', lazy: screen(() => import('../student/screens/Join'), STUDENT) },
      { path: 'join/:code', lazy: screen(() => import('../student/screens/Join'), STUDENT) },
      { path: 'c/:token', lazy: screen(() => import('../student/screens/CardSignIn'), STUDENT) },
      {
        path: 's',
        element: <RequireStudent />,
        children: [
          { index: true, lazy: screen(() => import('../student/screens/StudentHome'), STUDENT) },
        ],
      },

      // Families
      { path: 'f/:token', lazy: screen(() => import('../family/screens/FamilyView'), FAMILY) },

      // Family setup (docs/museum-spec.md §5)
      { path: 'setup', lazy: screen(() => import('../museum/screens/SetupScreen'), MUSEUM) },
      {
        path: 'm/phone/:token',
        lazy: screen(() => import('../museum/screens/PhoneUpload'), MUSEUM),
      },
      { path: 'm/l/:token', lazy: screen(() => import('../museum/screens/LinkScreen'), MUSEUM) },
      {
        path: 'm',
        element: <RequireHousehold />,
        children: [
          { index: true, lazy: screen(() => import('../museum/screens/MapScreen'), MUSEUM) },
          { path: 'f/:floorId', lazy: screen(() => import('../museum/screens/MapScreen'), MUSEUM) },
          {
            path: 'r/:roomId',
            lazy: screen(() => import('../museum/screens/MuseumRoomScreen'), MUSEUM),
          },
          {
            // A room's wall safe (the round 9 plan §9.2): those who may open the archive.
            path: 'r/:roomId/safe',
            lazy: screen(() => import('../museum/screens/RoomSafeScreen'), MUSEUM),
          },
          { path: 'w/:workId', lazy: screen(() => import('../museum/screens/WorkScreen'), MUSEUM) },
          {
            path: 'archive',
            lazy: screen(() => import('../museum/screens/ArchiveScreen'), MUSEUM),
          },
          {
            path: 'archive/box/:key',
            lazy: screen(() => import('../museum/screens/BoxScreen'), MUSEUM),
          },
          {
            path: 'archive/search',
            lazy: screen(() => import('../museum/screens/SearchScreen'), MUSEUM),
          },
          {
            path: 'office',
            element: <RequireCurator />,
            children: [
              {
                index: true,
                lazy: screen(() => import('../museum/office/screens/OfficeHome'), OFFICE),
              },
              {
                path: 'receiving',
                lazy: screen(() => import('../museum/office/screens/Receiving'), OFFICE),
              },
              {
                path: 'works/:workId',
                lazy: screen(() => import('../museum/office/screens/WorkEditor'), OFFICE),
              },
              {
                path: 'plans',
                lazy: screen(() => import('../museum/office/screens/FloorPlans'), OFFICE),
              },
              {
                // The Buildings tool (docs/site-buildings.md §8); static, so it outranks a floor.
                path: 'plans/buildings',
                lazy: screen(() => import('../museum/office/screens/FloorPlans'), OFFICE),
              },
              {
                path: 'plans/:floorId',
                lazy: screen(() => import('../museum/office/screens/FloorPlans'), OFFICE),
              },
              {
                path: 'rooms/:roomId',
                lazy: screen(() => import('../museum/office/screens/RoomManager'), OFFICE),
              },
              {
                path: 'people',
                lazy: screen(() => import('../museum/office/screens/People'), OFFICE),
              },
              {
                path: 'notes',
                lazy: screen(() => import('../museum/office/screens/Notes'), OFFICE),
              },
              {
                path: 'sharing',
                lazy: screen(() => import('../museum/office/screens/Sharing'), OFFICE),
              },
              {
                path: 'backup',
                lazy: screen(() => import('../museum/office/screens/Backup'), OFFICE),
              },
              {
                path: 'settings',
                lazy: screen(() => import('../museum/office/screens/Settings'), OFFICE),
              },
            ],
          },
        ],
      },
      // The Curator's Hub (family setup, level 2: docs/hub.md)
      { path: 'hub', lazy: screen(() => import('../hub/screens/HubHome'), HUB) },
      { path: 'hub/pair', lazy: screen(() => import('../hub/screens/PairScreen'), HUB) },
      // Hub administration: the hub's administrators only (others read "not available").
      { path: 'hub/admin', lazy: screen(() => import('../hub/screens/AdminScreen'), HUB) },
      // The page a sign-in email's link opens: a button signs in (links aren't used up by a GET).
      { path: 'hub/email', lazy: screen(() => import('../hub/screens/EmailLinkScreen'), HUB) },
      { path: 'hub/m/:id/guests', lazy: screen(() => import('../hub/screens/GuestsScreen'), HUB) },
      { path: 'visit', lazy: screen(() => import('../hub/screens/HubHome'), HUB) },
      { path: 'visit/:museumId', lazy: screen(() => import('../hub/screens/VisitScreen'), HUB) },

      // Development only: every design-system component on one page.
      ...(import.meta.env.DEV
        ? [
            {
              path: 'dev/components',
              lazy: screen(
                () => import('../design/ComponentGallery'),
                ['core', 'world', 'learn', 'teach', 'site'],
              ),
            },
          ]
        : []),

      { path: '*', lazy: screen(() => import('../public/NotFound'), PUBLIC) },
    ],
  },
]
